import { StatusBadge, EmptyState } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Wrench, Plus, Loader2, HardHat } from "lucide-react";
import { formatDateTime } from "@/lib/api";

export const RenterMaintenanceTab = ({
  maintenance, leaseProperties, maintForm, setMaintForm, maintOpen, setMaintOpen, onPhoto, submitMaint, busy,
}) => (
  <>
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-xl font-heading font-medium text-ink">Maintenance Requests</h2>
      <Dialog open={maintOpen} onOpenChange={setMaintOpen}>
        <DialogTrigger asChild>
          <button data-testid="add-maint-btn" disabled={leaseProperties.length === 0} className="bg-forest text-white px-4 py-2 rounded-lg font-medium hover:bg-forest-hover flex items-center gap-2 text-sm disabled:opacity-50">
            <Plus className="h-4 w-4" /> New Request
          </button>
        </DialogTrigger>
        <DialogContent className="bg-white rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Report a Maintenance Issue</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Property</Label>
              <select data-testid="maint-property" value={maintForm.property_id} onChange={(e) => setMaintForm({ ...maintForm, property_id: e.target.value })} className="mt-1 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm">
                <option value="">Select…</option>
                {leaseProperties.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div><Label>Title</Label><Input data-testid="maint-title" value={maintForm.title} onChange={(e) => setMaintForm({ ...maintForm, title: e.target.value })} className="mt-1 border-line" placeholder="Leaking kitchen faucet" /></div>
            <div><Label>Description</Label><Textarea data-testid="maint-desc" value={maintForm.description} onChange={(e) => setMaintForm({ ...maintForm, description: e.target.value })} className="mt-1 border-line" /></div>
            <div>
              <Label>Priority</Label>
              <select value={maintForm.priority} onChange={(e) => setMaintForm({ ...maintForm, priority: e.target.value })} className="mt-1 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm">
                <option value="low">Low</option><option value="medium">Medium</option><option value="urgent">Urgent</option>
              </select>
            </div>
            <div><Label>Photo (optional)</Label><Input type="file" accept="image/*" onChange={onPhoto} className="mt-1 border-line" /></div>
          </div>
          <DialogFooter>
            <button data-testid="submit-maint-btn" disabled={busy || !maintForm.property_id || !maintForm.title} onClick={submitMaint} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit Request
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    {maintenance.length === 0 ? (
      <div className="bg-white border border-line rounded-xl"><EmptyState icon={Wrench} title="No requests yet" subtitle="Report an issue and your landlord will be notified instantly." /></div>
    ) : (
      <div className="grid md:grid-cols-2 gap-4">
        {maintenance.map((m) => (
          <div key={m.id} data-testid={`renter-maint-${m.id}`} className="bg-white border border-line rounded-xl p-5">
            <div className="flex items-center gap-2"><StatusBadge status={m.priority} /><StatusBadge status={m.status} /></div>
            <h3 className="mt-2 font-medium text-ink">{m.title}</h3>
            <p className="text-sm text-mute">{m.property_title}</p>
            <p className="text-sm text-mute mt-2">{m.description}</p>
            {m.photo && <img src={m.photo} alt="" className="mt-3 rounded-lg max-h-40 object-cover" />}
            {m.assigned_trade !== undefined && m.dispatched_to && (
              <div data-testid={`renter-maint-assigned-${m.id}`} className="mt-3 flex items-start gap-2 rounded-lg bg-forest/[0.05] border border-forest/15 px-3 py-2">
                <HardHat className="h-4 w-4 text-forest mt-0.5 shrink-0" />
                <p className="text-sm text-ink">A {m.assigned_trade ? m.assigned_trade.toLowerCase() : "contractor"} has been assigned and will reach out to you soon.</p>
              </div>
            )}
            {m.response && <p className="text-sm text-forest mt-2">Landlord: {m.response}</p>}
            {(m.status_history || []).length > 0 && (
              <div className="mt-3 pt-3 border-t border-line">
                <p className="text-xs font-medium text-ink mb-2">Status timeline</p>
                <ol className="space-y-2">
                  {m.status_history.map((s, i) => {
                    const isLast = i === m.status_history.length - 1;
                    return (
                      <li key={`${s.label}-${s.at}`} className="flex items-start gap-2.5">
                        <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${isLast ? "bg-forest" : "bg-line"}`} />
                        <div className="min-w-0">
                          <p className={`text-sm ${isLast ? "text-ink font-medium" : "text-mute"}`}>{s.label}</p>
                          <p className="text-[11px] text-mute">{formatDateTime(s.at)}</p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </>
);
